import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { capitalize } from 'lodash'
import { useFormik } from 'formik'
import { useParams } from 'react-router-dom'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    IconButton,
    SelectChangeEvent,
} from '@mui/material'
import { Box } from '@mui/system'
import { Add } from '@mui/icons-material'
import { H6 } from '../../../../layouts/TextStyles'
import CustomInput from '../../../../components/muiComponents/customInput'
import SingleSelect from '../../../../components/muiComponents/singleSelect'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import { teacherApiSlice } from '../../../../features/teacher/teacherSlice'
import { groupSubjectApiSlice } from '../../../../features/group/groupSubjectSlice'
import { requiredId } from '../../../../utils/validation'
import { GroupSubjectState } from '../../../../features/types'
import { tone, ui } from '../../../../utils/uiTokens'

const styles = {
    error: {
        color: tone.bad.text,
        marginLeft: 2,
    },
}

interface Props {
    editMode?: boolean
    editSubject?: GroupSubjectState
    courseId: number
}

const AddGroupSubjectDialog: React.FC<Props> = ({
    editMode = false,
    editSubject = null,
    courseId,
}) => {
    const { groupId } = useParams()
    const { formatMessage, locale } = useIntl()
    const [openDialog, setOpenDialog] = useState(false)
    const [createGroupSubject, { isLoading: isCreating }] =
        groupSubjectApiSlice.useCreateGroupSubjectMutation()
    const [updateGroupSubject, { isLoading: isUpdating }] =
        groupSubjectApiSlice.useUpdateGroupSubjectMutation()
    const {
        data: activeCourseSubjects = [],
        isFetching: isFetchingCourseSubjects,
    } = courseSubjectApiSlice.useFetchActiveCourseSubjectsQuery(courseId, {
        skip: !openDialog,
    })

    const { teachers, isFetchingTeachers } =
        teacherApiSlice.useFetchAllTeachersQuery(
            {
                query: `page=${0}&size=${10000}`,
                roles: ['TEACHER'],
            },
            {
                selectFromResult: ({ data, isFetching }) => ({
                    teachers: data?.data,
                    isFetchingTeachers: isFetching,
                }),
            },
        )

    const groupSubject: GroupSubjectState = {
        id: editSubject?.id || null,
        teacherId: editSubject?.teacherId || '',
        lessonLink: editSubject?.lessonLink || '',
        lessonComment: editSubject?.lessonComment || '',
        courseSubjectId: editSubject?.courseSubjectId || '',
    }

    const validationSchema = yup.object().shape({
        courseSubjectId: !editMode ? requiredId({ formatMessage }) : null,
    })

    const formik = useFormik({
        initialValues: groupSubject,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editSubject) {
                handleUpdateGroupSubject()
            } else {
                handleCreateGroupSubject()
            }
        },
    })

    const handleCreateGroupSubject = async () => {
        try {
            await createGroupSubject({
                groupId: Number(groupId),
                groupSubject: formik.values,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            formik.resetForm()
            handleCloseDialog()
        } catch (error) {}
    }

    const handleUpdateGroupSubject = async () => {
        try {
            await updateGroupSubject(formik.values).unwrap()
            toast.success(formatMessage({ id: 'updatedSuccess' }))
            handleCloseDialog()
        } catch (error) {}
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
    }

    const handleClickOpen = () => {
        setOpenDialog(true)
    }

    const handleSubjectChange = (
        event: SelectChangeEvent<typeof formik.values.courseSubjectId>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('courseSubjectId', value)
    }

    const handleTeacherChange = (
        event: SelectChangeEvent<typeof formik.values.teacherId>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('teacherId', value)
    }

    return (
        <div>
            {editMode ? (
                <IconButton
                    sx={{ background: ui.track }}
                    onClick={handleClickOpen}
                >
                    <img
                        src={EditIcon}
                        alt="edit"
                    />
                </IconButton>
            ) : (
                <Button
                    variant="contained"
                    endIcon={<Add />}
                    onClick={handleClickOpen}
                >
                    {formatMessage({ id: 'addSubject' })}
                </Button>
            )}
            <Dialog
                maxWidth={'sm'}
                fullWidth
                open={openDialog}
                onClose={handleCloseDialog}
            >
                <DialogTitle id="responsive-dialog-title">
                    {formatMessage({ id: 'addSubject' })}
                </DialogTitle>

                {isFetchingCourseSubjects || isFetchingTeachers ? (
                    <CircularProgress sx={{ ml: 3 }} />
                ) : (
                    <DialogContent>
                        <form onSubmit={formik.handleSubmit}>
                            <Box sx={{ width: '100%', mr: '10px' }}>
                                <Box mb={2}>
                                    <H6>{formatMessage({ id: 'subject' })}</H6>

                                    <SingleSelect
                                        id="courseSubjectId"
                                        name="courseSubjectId"
                                        placeholder={
                                            editMode
                                                ? editSubject[
                                                      `subjectTitle${capitalize(
                                                          locale,
                                                      )}`
                                                  ]
                                                : formatMessage({
                                                      id: 'subject',
                                                  })
                                        }
                                        value={
                                            !editMode
                                                ? formik.values.courseSubjectId
                                                : null
                                        }
                                        handleChange={handleSubjectChange}
                                        menuItems={activeCourseSubjects}
                                        handleBlur={formik.handleBlur}
                                        handleError={Boolean(
                                            formik.errors.courseSubjectId,
                                        )}
                                        disabled={editMode}
                                    />

                                    <FormHelperText sx={styles.error}>
                                        {formik.touched.courseSubjectId &&
                                            formik.errors.courseSubjectId}
                                    </FormHelperText>
                                </Box>

                                <Box mb={2}>
                                    <H6>{formatMessage({ id: 'teacher' })}</H6>

                                    <SingleSelect
                                        id="teacherId"
                                        name="teacherId"
                                        placeholder={formatMessage({
                                            id: 'teacher',
                                        })}
                                        value={formik.values.teacherId}
                                        handleChange={handleTeacherChange}
                                        menuItems={teachers}
                                    />
                                </Box>

                                <Box mb={2}>
                                    <H6>{formatMessage({ id: 'link' })}</H6>

                                    <CustomInput
                                        id="lessonLink"
                                        name="lessonLink"
                                        placeholder={formatMessage({
                                            id: 'link',
                                        })}
                                        fullWidth
                                        value={formik.values.lessonLink}
                                        onChange={formik.handleChange}
                                        margin="dense"
                                    />
                                </Box>

                                <Box mb={2}>
                                    <H6>{formatMessage({ id: 'comment' })}</H6>

                                    <CustomInput
                                        id="lessonComment"
                                        name="lessonComment"
                                        placeholder={formatMessage({
                                            id: 'comment',
                                        })}
                                        fullWidth
                                        value={formik.values.lessonComment}
                                        onChange={formik.handleChange}
                                        margin="dense"
                                    />
                                </Box>
                            </Box>
                        </form>
                    </DialogContent>
                )}
                <DialogActions sx={{ m: 2 }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isCreating || isUpdating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        onClick={() => formik.handleSubmit()}
                        disabled={isCreating || isUpdating}
                    >
                        {isCreating || isUpdating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddGroupSubjectDialog
